import type { UseWindowSizeOptions } from '@vueuse/core'
import { useWindowSize as useBrowserWindowSize } from '@vueuse/core'
import { calculateAutoLayoutColumnsForWidth } from '../utils/layout/calculateAutoLayoutColumnsForWidth'
import { isHydratingServerRender } from '../utils/render/hydration'

/**
 * The viewport the server laid the page out for, carried in the payload so the
 * hydrating browser can lay out at the same width. Null on a page the server
 * did not render.
 */
export const RENDER_VIEWPORT_STATE = 'render-viewport'

type Viewport = { width: number; height: number }

/**
 * Viewport a server render assumes for a browser it cannot measure. The layout
 * only needs the column count right for the common case: 1366 px gives four
 * card columns (the same as any laptop up to 1595 px) and 390 px gives one.
 * A wider or narrower browser corrects itself on hydration.
 */
export const SERVER_VIEWPORT = {
  desktop: { width: 1366, height: 768 },
  mobile: { width: 390, height: 844 },
} as const

const MOBILE_UA = /Mobi|Android|iPhone|iPad|iPod/i

/**
 * A hint is only usable if the layout can fit at least one card column in it.
 * Anything narrower (a stray `Sec-CH-Viewport-Width: 1`) yields zero columns
 * and zero shortcuts per row, so the user agent guess is the safer answer.
 */
export function isUsableViewportWidth(width: number): boolean {
  return (
    Number.isFinite(width) && calculateAutoLayoutColumnsForWidth(width) >= 1
  )
}

/**
 * The viewport a server render works from: the width the browser advertised in
 * the Sec-CH-Viewport-Width hint when it sent a usable one, otherwise a guess
 * from the user agent.
 */
export function guessServerViewport(
  userAgent: string | undefined,
  viewportWidthHint?: string
) {
  const base = MOBILE_UA.test(userAgent ?? '')
    ? SERVER_VIEWPORT.mobile
    : SERVER_VIEWPORT.desktop
  const hinted = Number(viewportWidthHint)
  if (isUsableViewportWidth(hinted)) {
    return { width: hinted, height: base.height }
  }
  return base
}

/**
 * The viewport a render uses for a group's viewport bucket.
 *
 * The narrowest bucket represents browsers under 348 px, and the layout fits no
 * column at all in that, so its representative width is not renderable. Falling
 * back to the user agent there would key the render on something the group does
 * not carry, and two visitors in one group would be served each other's page,
 * so the narrowest renderable viewport is used instead. It is the same answer
 * for everyone in the bucket, which is the property that matters.
 */
export function groupViewport(bucketed: number) {
  if (isUsableViewportWidth(bucketed)) {
    return { width: bucketed, height: SERVER_VIEWPORT.desktop.height }
  }
  return SERVER_VIEWPORT.mobile
}

function serverViewport() {
  // The group is the only thing a cached render may depend on, and it carries a
  // viewport bucket. The header path stays for a request the gate did not
  // resolve a group for, which is the client shell and is never cached per
  // visitor anyway.
  const group = useRequestEvent()?.context.ssrGroup
  const bucketed = group?.attributes.viewport
  if (typeof bucketed === 'number') {
    return groupViewport(bucketed)
  }
  const headers = useRequestHeaders(['user-agent', 'sec-ch-viewport-width'])
  return guessServerViewport(
    headers['user-agent'],
    headers['sec-ch-viewport-width']
  )
}

/**
 * Drop-in for `useWindowSize` from @vueuse/core.
 *
 * During a server render it starts from the request's viewport instead of
 * Infinity, so the column count and the ad placement come out right for the
 * group, and it puts that viewport in the payload.
 *
 * While the browser hydrates a server render it reports that same viewport,
 * not the window's, because the markup it has to reproduce was laid out for
 * it. A cookie written before the window was resized, or a reload that beat
 * the rewrite, would otherwise hydrate a page built for one bucket at another
 * bucket's width, a mismatch on every card row. Once hydration is over the
 * size follows the real window, and that is a reactive re-render rather than
 * a correction Vue has to make. Everywhere else it is the vueuse composable,
 * unchanged.
 */
export function useWindowSize(options: UseWindowSizeOptions = {}) {
  if (import.meta.server) {
    const viewport = serverViewport()
    useState<Viewport | null>(RENDER_VIEWPORT_STATE, () => viewport)
    return useBrowserWindowSize({
      initialWidth: viewport.width,
      initialHeight: viewport.height,
      ...options,
    })
  }

  const live = useBrowserWindowSize(options)
  if (!isHydratingServerRender()) return live

  const rendered = useState<Viewport | null>(RENDER_VIEWPORT_STATE, () => null)
  if (!rendered.value) return live

  const width = shallowRef(rendered.value.width)
  const height = shallowRef(rendered.value.height)
  const follow = () => {
    width.value = live.width.value
    height.value = live.height.value
  }
  useNuxtApp().hook('app:suspense:resolve', () => {
    follow()
    watch([live.width, live.height], follow)
  })
  return { width, height }
}
